import React from 'react';
import PropTypes from 'prop-types';
import * as ReactRedux from 'react-redux';
import * as Actions from 'actions';
import ImageButton from 'ImageButton';
import {
  addFieldToSectionOrganizer,
  addSectionToSectionOrganizer,
  removeFieldFromSectionOrganizer,
} from 'SelfServePostBuilderSidePanel/SectionOrganizer/SectionOrganizerActions';
import withSelfServeDragAndResize from 'withSelfServeDragAndResize';
import { bindActionCreators } from 'redux';
import { enablePopupCreation, disablePopupCreation, setFirstSamplePopupMounted } from 'SelfServeBuilder/SelfServeBuilderActions';
import { SelfServeBuilderPopupTypes, toolboxItemTypeToTracking } from 'Popups';
import { setActiveToolboxToolItem } from 'SelfServeBuilderToolbox/SelfServeBuilderToolboxActions';
import { clearActiveGroupPopup } from 'PopupGroup/PopupGroupActions';
import BoxPopupNameInput from './BoxPopupNameInput';


const TOOLTIP_ERROR_HEIGHT = 105;
const MIN_BOX_POPUP_WIDTH_DIFF = 52;
const TEXT_AREA_PADDING = 20;
const { SIGNATURE } = SelfServeBuilderPopupTypes;

const withSelfServeCreator = (WrappedComponent, withSelfServeCreatorProps = {}) => {
  class WrappedComponentWithSelfServeCreator extends React.Component {
    constructor(props) {
      super(props);

      this.isActive = this.isActive.bind(this);
      this.onDelete = this.onDelete.bind(this);
      this.onDone = this.onDone.bind(this);
      this.onEdit = this.onEdit.bind(this);
      this.onBoxNameChange = this.onBoxNameChange.bind(this);
      this.onBoxPopupNameInputResizeHeight = this.onBoxPopupNameInputResizeHeight.bind(this);
      this.setActiveToolboxToolItem = this.setActiveToolboxToolItem.bind(this);
      this.hideError = this.hideError.bind(this);
      this.state = {
        showError: false,
        inputHeight: 0,
      };
    }

    shouldComponentUpdate(nextProps, nextState) {
      const {
        activePopup: currentActiveId,
        zoomFactor: currentZoomFactor,
        userData: currentUserData,
        isSelected: currentIsSelected,
      } = this.props;
      const {
        activePopup: nextActiveId,
        zoomFactor: nextZoomFactor,
        userData: nextUserData,
        isSelected: nextIsSelected,
      } = nextProps;
      const stateChanged = JSON.stringify(nextState) !== JSON.stringify(this.state);
      const userDataChanged = currentUserData !== nextUserData;
      const activeIdChanged = this.props.id === currentActiveId || this.props.id === nextActiveId;
      const zoomFactorChanged = currentZoomFactor !== nextZoomFactor;
      const isSelectedChanged = currentIsSelected !== nextIsSelected;
      return activeIdChanged || stateChanged || zoomFactorChanged || userDataChanged || isSelectedChanged;
    }

    componentDidUpdate(prevProps) {
      if (this.props.showPopupCreationError !== prevProps.showPopupCreationError) {
        const showError = this.props.showPopupCreationError;
        this.setState({ showError });

        if (showError) {
          this.props.addPaddingTop(this.props.id, this.state.inputHeight + TOOLTIP_ERROR_HEIGHT);
        } else {
          this.props.addPaddingTop(this.props.id, 0);
        }
      } else if (this.props.y !== prevProps.y) {
        this.props.addPaddingTop(this.props.id, this.state.inputHeight);
      }

      if (!this.props.isEditingGroup && this.props.activeGroupId) {
        this.props.clearActiveGroupPopup();
      }
    }

    onDelete(e) {
      this.props.addPaddingTop(this.props.id, 0);
      this.props.removeFieldFromSectionOrganizer(this.props.id, this.props.data.type === SIGNATURE);
      this.props.onDelete(this.props.id);
      this.props.enablePopupCreation();
      // Stop propagation to prevent setActivePopop onclick on wrapping div to fire
      e.stopPropagation();
    }

    onDone() {
      const isSignature = this.props.data.type === SIGNATURE;
      if (isSignature) {
        this.props.onAttributesChanged(this.props.id, {
          isAnimationDisabled: true,
        });
        // Don't repeatedly add a signature section
        if (!this.props.data.isAnimationDisabled) {
          this.props.addSectionToSectionOrganizer('Add Your Signature', false, true, false, true);
        }
      }
      this.props.addFieldToSectionOrganizer(this.props.id);
      this.props.enablePopupCreation();
      this.props.endEditingPopup();
      if (!this.props.data.isAnimationDisabled) {
        window._track(`Done | ${toolboxItemTypeToTracking[this.props.data.type]}`, 'Template Builder');
      }
      this.setState({ showError: false });
    }

    onEdit() {
      this.props.disablePopupCreation();
      this.props.beginEditingPopup();
    }

    onBoxPopupNameInputResizeHeight(height) {
      const inputHeight = height + TEXT_AREA_PADDING;
      this.setState({
        inputHeight,
      });
      this.props.addPaddingTop(this.props.id, inputHeight);
    }

    onBoxNameChange(name) {
      this.props.onAttributesChanged(this.props.id, {
        label: name,
      });
    }

    setActiveToolboxToolItem() {
      const activePopup = this.props.data;
      const activePopupType = activePopup.type;
      if (!this.props.isEditingGroup && this.props.activeToolboxItem !== activePopupType) {
        this.props.setActiveToolboxToolItem(activePopupType);
      }
    }

    isActive() {
      if (this.props.isPreviewMode) return false;
      return this.props.activePopup === this.props.id;
    }

    hideError() {
      this.setState({
        showError: false,
      });
    }

    render() {
      const { isPreviewMode, id } = this.props;
      const isActive = this.isActive();
      const isSignature = this.props.data.type === SIGNATURE;
      let zoomFactor = this.props.zoomFactor;
      if (!isPreviewMode) {
        zoomFactor = 1;
      }
      return (
        <div
          id={isPreviewMode ? `preview-${id}` : id}
          className="self-serve-creator-popup"
        >
          {isActive &&
            <ImageButton
              className="delete-img-btn"
              noMargin={false}
              onClick={this.onDelete}
              alt="Delete"
              src="/images/self-serve-builder/delete-box-2x.png"
              srcHover="/images/self-serve-builder/delete-box-hover-2x.png"
            />
          }
          <div
            className="drag-handle"
            onMouseDown={this.setActiveToolboxToolItem}
          >
            {isActive &&
              <React.Fragment>
                <div
                  style={{
                    position: 'absolute',
                    bottom: this.props.height,
                  }}
                >
                  <BoxPopupNameInput
                    id={this.props.id}
                    isEditable={!isSignature}
                    isFirstSamplePopupMounted={this.props.isFirstSamplePopupMounted}
                    isSampleState={this.props.isSampleState}
                    error={this.state.showError}
                    onBoxNameChange={this.onBoxNameChange}
                    value={this.props.data.label}
                    onDone={this.onDone}
                    onEdit={this.onEdit}
                    onBoxPopupNameInputResizeHeight={this.onBoxPopupNameInputResizeHeight}
                    width={this.props.width - MIN_BOX_POPUP_WIDTH_DIFF}
                    getSuperScriptValue={this.props.getSuperScriptValue}
                    disablePopupCreation={this.props.disablePopupCreation}
                    hideError={this.hideError}
                    label={this.props.data.label}
                    doneOnRender={isSignature}
                    setFirstSamplePopupMounted={this.props.setFirstSamplePopupMounted}
                  />
                </div>
                <WrappedComponent
                  isActive
                  height={this.props.height}
                  width={this.props.width}
                />
              </React.Fragment>
            }

            {!isActive &&
              <WrappedComponent
                isActive={false}
                isSelected={this.props.isSelected}
                height={this.props.height}
                width={this.props.width}
                userData={this.props.userData}
                zoomFactor={zoomFactor}
                isPreviewMode={this.props.isPreviewMode}
              />
            }
          </div>
          {!isPreviewMode &&
            <React.Fragment>
              {this.props.resizeHandleComponent}
            </React.Fragment>
          }
        </div>
      );
    }
  }

  WrappedComponentWithSelfServeCreator.defaultProps = {
    activeGroupId: '',
    activePopup: '',
    activeToolboxItem: '',
    isSampleState: false,
    isSelected: false,
    resizeHandleComponent: null,
    setFirstSamplePopupMounted: () => null,
    showPopupCreationError: false,
    userData: '',
  };

  WrappedComponentWithSelfServeCreator.propTypes = {
    activeGroupId: PropTypes.string,
    activePopup: PropTypes.string,
    activeToolboxItem: PropTypes.string,
    addFieldToSectionOrganizer: PropTypes.func.isRequired,
    addPaddingTop: PropTypes.func.isRequired,
    addSectionToSectionOrganizer: PropTypes.func.isRequired,
    beginEditingPopup: PropTypes.func.isRequired,
    clearActiveGroupPopup: PropTypes.func.isRequired,
    data: PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      isAnimationDisabled: PropTypes.bool,
    }).isRequired,
    disablePopupCreation: PropTypes.func.isRequired,
    enablePopupCreation: PropTypes.func.isRequired,
    endEditingPopup: PropTypes.func.isRequired,
    getSuperScriptValue: PropTypes.func.isRequired,
    height: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    isPreviewMode: PropTypes.bool.isRequired,
    isEditingGroup: PropTypes.bool.isRequired,
    isFirstSamplePopupMounted: PropTypes.bool.isRequired,
    isSampleState: PropTypes.bool,
    isSelected: PropTypes.bool,
    onAttributesChanged: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    removeFieldFromSectionOrganizer: PropTypes.func.isRequired,
    resizeHandleComponent: PropTypes.node,
    setActiveToolboxToolItem: PropTypes.func.isRequired,
    setFirstSamplePopupMounted: PropTypes.func,
    showPopupCreationError: PropTypes.bool,
    userData: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        type: PropTypes.string,
        url: PropTypes.string,
      }),
    ]),
    width: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    zoomFactor: PropTypes.number.isRequired,
  };

  const mapStateToProps = ({
    popupHandler,
    SelfServeBuilderReducer,
    SelfServeBuilderToolboxReducer,
    PopupGroupReducer,
    StaticBuilderReducer,
  }, ownProps) => ({
    activeGroupId: PopupGroupReducer.activeGroupId,
    activePopup: popupHandler.activePopup,
    activeToolboxItem: SelfServeBuilderToolboxReducer.activeToolboxItem,
    height: ownProps.data.height,
    isEditingGroup: PopupGroupReducer.isEditingGroup,
    isFirstSamplePopupMounted: SelfServeBuilderReducer.isFirstSamplePopupMounted,
    isSampleState: SelfServeBuilderReducer.isSampleState,
    showPopupCreationError: SelfServeBuilderReducer.showPopupCreationError,
    userData: StaticBuilderReducer.fieldData[ownProps.id] || null,
    width: ownProps.data.width,
    x: ownProps.data.x,
    y: ownProps.data.y,
    zIndex: ownProps.data.zIndex,
  });

  const mapDispatchToProps = dispatch => (
    bindActionCreators({
      beginEditingPopup: Actions.beginEditingPopup,
      endEditingPopup: Actions.endEditingPopup,
      onMove: Actions.movePopup,
      onAttributesChanged: Actions.changePopup,
      onDelete: Actions.deletePopup,
      clearActiveGroupPopup,
      enablePopupCreation,
      disablePopupCreation,
      addFieldToSectionOrganizer,
      addSectionToSectionOrganizer,
      removeFieldFromSectionOrganizer,
      setActiveToolboxToolItem,
      setFirstSamplePopupMounted,
    }, dispatch)
  );

  return ReactRedux.connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withSelfServeDragAndResize(WrappedComponentWithSelfServeCreator,
    withSelfServeCreatorProps.withDragAndResizeProps || {},
  ));
};

export default withSelfServeCreator;



// WEBPACK FOOTER //
// ./src-react/withSelfServeCreator/index.jsx